import React, { useState } from "react";

import { Button } from "~/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";

interface PrepaidApprovalActionsProps {
  selectedOrder: SalesRequest;
  clientName: string;
  onOrderStatusChange?: () => void;
}

export const PrepaidApprovalActions: React.FC<PrepaidApprovalActionsProps> = ({
  selectedOrder,
  clientName,
  onOrderStatusChange,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmSheetOpen, setIsConfirmSheetOpen] = useState(false);
  const [isRegisterPaymentSheetOpen, setIsRegisterPaymentSheetOpen] =
    useState(false);
  const profileStore = useProfileStore();

  const isAdminUser =
    profileStore.role === "superadmin" || profileStore.role === "admin";
  const isSeller = profileStore.role === "seller";
  const isPrepaid = selectedOrder.status === "prepaid";

  // Show for sellers and admins when order is in prepaid status
  if ((!isAdminUser && !isSeller) || !isPrepaid) {
    return null;
  }

  const handleRegisterPayment = async () => {
    setIsSubmitting(true);
    try {
      // Update status from "prepaid" to "payment_to_check"
      const { error } = await supabase
        .from("sales_request")
        .update({
          status: "payment_to_check",
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Trigger logs the change; just attach the reason to that row.
      await supabase.rpc("annotate_latest_status_change", {
        p_sales_request_id: selectedOrder.id,
        p_new_status: "payment_to_check",
        p_comment: "Pago registrado por vendedor",
      });

      toast({
        title: "Pago registrado",
        description:
          "El pago ha sido registrado. Pendiente de validación por cobranza.",
      });

      setIsRegisterPaymentSheetOpen(false);
      if (onOrderStatusChange) onOrderStatusChange();
    } catch (error) {
      console.error("Error registering payment:", error);
      toast({
        title: "Error",
        description: "No se pudo registrar el pago",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualApproval = async () => {
    setIsSubmitting(true);
    try {
      // First, update the sales request status to 'accepted' and mark as paid
      const { error } = await supabase
        .from("sales_request")
        .update({
          status: "accepted",
          paid: true, // Mark as paid for manual approval
          approved_rejected_by: profileStore.id,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Trigger logs the change; just attach the reason to that row.
      await supabase.rpc("annotate_latest_status_change", {
        p_sales_request_id: selectedOrder.id,
        p_new_status: "accepted",
        p_comment: "Aprobado manualmente sin esperar pago",
      });

      toast({
        title: "Nota aprobada manualmente",
        description: "La nota de venta ha sido aprobada sin esperar el pago",
        variant: "default",
      });

      setIsConfirmSheetOpen(false);
      if (onOrderStatusChange) onOrderStatusChange();
    } catch (error) {
      console.error("Error approving order:", error);
      toast({
        title: "Error",
        description: "No se pudo aprobar la nota de venta",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Sheet open={isConfirmSheetOpen} onOpenChange={setIsConfirmSheetOpen}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Aprobar Nota de Venta Manualmente</SheetTitle>
            <SheetDescription>
              ¿Está seguro que desea aprobar esta nota de venta sin esperar
              confirmación de pago?
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 flex flex-col gap-6">
            <div className="grid gap-2">
              <div className="mb-1 font-medium">Información de la Nota</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">Cliente:</span>
                <span>{clientName || "Cliente"}</span>
                <span className="text-muted-foreground">Total:</span>
                <span>${selectedOrder.totalAmount.toLocaleString()}</span>
                <span className="text-muted-foreground">Estado actual:</span>
                <span className="font-medium text-amber-600">
                  Esperando pago
                </span>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setIsConfirmSheetOpen(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleManualApproval}
                className="bg-green-600 hover:bg-green-700"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Cargando..." : "Confirmar Aprobación Manual"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet
        open={isRegisterPaymentSheetOpen}
        onOpenChange={setIsRegisterPaymentSheetOpen}
      >
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Registrar Pago Anticipado</SheetTitle>
            <SheetDescription>
              ¿Confirma que el cliente ha realizado el pago anticipado para esta
              nota de venta?
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 flex flex-col gap-6">
            <div className="grid gap-2">
              <div className="mb-1 font-medium">Información de la Nota</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">Cliente:</span>
                <span>{clientName || "Cliente"}</span>
                <span className="text-muted-foreground">Total:</span>
                <span>${selectedOrder.totalAmount.toLocaleString()}</span>
                <span className="text-muted-foreground">Estado actual:</span>
                <span className="font-medium text-amber-600">
                  Anticipado Pendiente de Pago
                </span>
              </div>
            </div>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <p className="text-sm text-blue-800">
                Al confirmar, la nota de venta pasará a "Validación Pago" donde
                el equipo de cobranza verificará que el pago sea correcto antes
                de aprobar definitivamente.
              </p>
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setIsRegisterPaymentSheetOpen(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleRegisterPayment}
                className="bg-green-600 hover:bg-green-700"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Registrando..." : "Confirmar Pago Recibido"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
